import type { AgentTargetStatsBucket, UserRole } from '@utazzo/shared';

import { useAgentTargetStats } from '@/features/agent-targets/use-agent-targets';
import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * Business Numbers strip. Reads pre-aggregated stats from
 * `GET /agent-targets/stats` (ADR-0043), which is RBAC-scoped
 * server-side. Each metric (Target / Achievement / Files Converted /
 * Short) renders as ONE split tile with a vertical divider:
 *
 *   SALES_AGENT       -> tile shows only the "Mine" side (no split)
 *   SALES_LEAD        -> tile shows "Mine | Team" with a vertical divider
 *   ADMIN / OPS /     -> tile shows only the org-wide "Team" side (no split)
 *   FINANCE / READ_ONLY
 *   CONTENT_EDITOR /  -> strip hidden (no sales quota)
 *   MARKETING_LEAD
 *
 * The strip is pinned to TURNOVER basis server-side and the basis
 * label is intentionally NOT surfaced - the operator sees a single
 * "Target" number regardless of how admins flagged the master row.
 * Admins still pick TURNOVER vs REVENUE in the form for accounting
 * clarity, but that distinction stays out of the day-to-day view.
 */

/**
 * Full-digit INR formatter using the Indian grouping convention
 * (1 Lakh = 1,00,000 · 1 Crore = 1,00,00,000). The full number is
 * shown - no K / L / Cr abbreviations - so the operator reads the
 * exact rupee figure at a glance.
 *
 *   1,250          -> ₹1,250
 *   80,000         -> ₹80,000
 *   5,50,000       -> ₹5,50,000
 *   1,25,00,000    -> ₹1,25,00,000
 *
 * `tabular-nums` on the rendering span keeps long numbers aligned
 * across the four-tile grid.
 */
const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});
const formatInr = (amount: number): string => INR_FORMATTER.format(amount);

const COUNT_FORMATTER = new Intl.NumberFormat('en-IN');
const formatCount = (n: number): string => COUNT_FORMATTER.format(n);

// SALES_LEAD carries personal quota AND manages a team. ADMIN/OPS/
// FINANCE/READ_ONLY see org-wide aggregates only - no "My" column.
const ROLES_THAT_SEE_STRIP: ReadonlyArray<UserRole> = [
  'SALES_AGENT',
  'SALES_LEAD',
  'ADMIN',
  'OPS',
  'FINANCE',
  'READ_ONLY',
];
type StripMode = 'my-only' | 'team-only' | 'split';

const stripModeFor = (role: UserRole | null): StripMode => {
  if (role === 'SALES_AGENT') return 'my-only';
  if (role === 'SALES_LEAD') return 'split';
  return 'team-only';
};

// Claude Design "Lead Inbox" mockup: the Targets card is one bordered
// panel (radius 14) with a Target / Achievement / Files converted (Won) /
// Short column row, an indigo achievement progress bar, and a pace-needed
// foot line. Exact hexes are inlined to match the mockup pixel-for-pixel
// (the admin's token aesthetic does not carry the indigo/teal split the
// mockup uses here).
const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e4e4e7',
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(0,0,0,.04)',
  padding: '16px 18px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const COL_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
};
const COL_DIVIDED_STYLE: React.CSSProperties = {
  ...COL_STYLE,
  borderLeft: '1px solid #f4f4f5',
  paddingLeft: 16,
};
const COL_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: '#71717a',
};
const BIG_NUM_STYLE: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 600,
  letterSpacing: '-.02em',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.1,
};
const BIG_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  flexWrap: 'wrap',
};
const SUB_STYLE: React.CSSProperties = { fontSize: 12, color: '#71717a' };
const PCT_PILL_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#4338ca',
  background: '#eef2ff',
  borderRadius: 999,
  padding: '2px 8px',
};

interface LeadsBusinessStripProps {
  /**
   * 2026-07-13. Owner filter driven by the inbox's "All owners"
   * dropdown. When set, the strip re-scopes to the picked user's
   * numbers instead of the caller's own (server enforces visibility).
   * The strip's mode also collapses to "my-only" for a picked
   * SALES_AGENT (no team to show) and to "split" for a picked
   * SALES_LEAD (self + their reports). Omit / null for the classic
   * caller-scoped view.
   */
  ownerId?: string | null;
  /**
   * Role of the picked owner. Passed from the parent because the
   * caller already has the resolved user object from the owner
   * dropdown - the strip uses it to pick the right rendering mode
   * without a second lookup. Ignored when `ownerId` is not set.
   */
  ownerRole?: 'SALES_AGENT' | 'SALES_LEAD' | null;
  /**
   * Display name of the picked owner. Prefixed onto each tile's
   * caption ("Poonam Target", "Poonam Achievement", ...) so the
   * operator always sees whose numbers are on screen. Falls back
   * to a generic "Owner" prefix when omitted. Ignored when
   * `ownerId` is not set.
   */
  ownerName?: string | null;
  /**
   * 2026-07-21. Conversion-date window for the "Files Converted (Won)"
   * tile ONLY, driven by the inbox's date-range filter. Both-or-neither
   * (YYYY-MM-DD). When set, the won COUNT re-scopes to leads converted in
   * that window while Target / Achievement / Short stay on the monthly
   * quota (ADR-0043). The parent only passes these for a non-default
   * range, so the default "this month" view is byte-identical to before.
   */
  wonFrom?: string | null;
  wonTo?: string | null;
  /**
   * Human label for the active range ("Last 7 days", "All time", ...).
   * When present alongside `wonFrom`/`wonTo`, a footnote clarifies that
   * only Files Converted follows the range. Omit to hide the footnote.
   */
  rangeLabel?: string | null;
  /**
   * Monthly quota period to report on (YYYY-MM-01). The Lead Inbox omits
   * this (always the current month), but the Reports page is per-month, so it
   * passes the selected month's first day to make Target / Achievement / Short
   * reflect THAT month instead of the current one.
   */
  periodStart?: string | null;
}

export const LeadsBusinessStrip = ({
  ownerId,
  ownerRole,
  ownerName,
  wonFrom,
  wonTo,
  rangeLabel,
  periodStart,
}: LeadsBusinessStripProps = {}): React.JSX.Element | null => {
  const me = useCurrentUser();
  // Only a complete window re-scopes the count; a lone bound is ignored
  // (matches the server's both-or-neither rule).
  const wonWindowActive = Boolean(wonFrom && wonTo);

  const role = me?.role ?? null;
  const seesStrip = role !== null && ROLES_THAT_SEE_STRIP.includes(role);

  // 2026-07-13. When an owner is picked in the inbox dropdown, the
  // strip re-scopes to that user - SALES_AGENT owner => single tile
  // (their numbers), SALES_LEAD owner => split (their own + their
  // team). Otherwise use the caller-role-based mode as before.
  const mode: StripMode = ownerId
    ? ownerRole === 'SALES_LEAD'
      ? 'split'
      : 'my-only'
    : stripModeFor(role);

  // 2026-08-04. Basis is pinned to TURNOVER (full booking value / TTV) - the
  // canonical target + achievement basis across all reporting. Most agent
  // targets are stored as TURNOVER; REVENUE (profit-only) is deferred, so the
  // strip and the Business-Number reports both count on turnover / TTV.
  const statsQuery = useAgentTargetStats({
    period: 'MONTHLY',
    basis: 'TURNOVER',
    ...(periodStart ? { periodStart } : {}),
    ...(ownerId ? { userId: ownerId } : {}),
    // Files Converted (Won) follows the inbox range; the money tiles stay
    // on the monthly period server-side regardless of this.
    ...(wonWindowActive ? { wonFrom: wonFrom ?? undefined, wonTo: wonTo ?? undefined } : {}),
  });
  const data = statsQuery.data;
  const loading = statsQuery.isLoading;

  if (!seesStrip) return null;

  // Pick the headline bucket by mode. SALES_LEAD (split) shows its own
  // numbers as the hero and the team as a compact secondary line.
  const primary: AgentTargetStatsBucket | null =
    mode === 'team-only' ? (data?.team ?? null) : (data?.my ?? null);
  const secondary: AgentTargetStatsBucket | null = mode === 'split' ? (data?.team ?? null) : null;

  const target = primary?.targetAmount ?? 0;
  const achievement = primary?.achievementAmount ?? 0;
  const won = primary?.wonLeadCount ?? 0;
  const short = primary?.shortAmount ?? 0;
  const achievementPct = target > 0 ? (achievement / target) * 100 : 0;
  const avgPerFile = won > 0 ? achievement / won : 0;

  // Days left in the quota month. Only the current month has runway; a
  // past month (Reports page) is already closed, so pace is not shown.
  const now = new Date();
  const period = periodStart
    ? new Date(periodStart)
    : new Date(now.getFullYear(), now.getMonth(), 1);
  const isCurrentMonth =
    period.getFullYear() === now.getFullYear() && period.getMonth() === now.getMonth();
  const daysInMonth = new Date(period.getFullYear(), period.getMonth() + 1, 0).getDate();
  const daysLeft = isCurrentMonth ? Math.max(0, daysInMonth - now.getDate()) : 0;
  const paceNeeded = daysLeft > 0 ? short / daysLeft : 0;

  // Owner-picked view: name the owner on the card (the section label only
  // says "Targets"). First word keeps it compact.
  const ownerLabel = (ownerName?.trim().split(/\s+/)[0] ?? '') || 'Owner';

  return (
    <div className="mb-3">
      <div style={CARD_STYLE}>
        {ownerId ? (
          <div style={{ fontSize: 11, fontWeight: 600, color: '#71717a' }}>
            Showing <span style={{ color: '#094053' }}>{ownerLabel}</span>
          </div>
        ) : null}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
            gap: 16,
          }}
        >
          <div style={COL_STYLE}>
            <div style={COL_LABEL_STYLE}>Target</div>
            <div style={{ ...BIG_NUM_STYLE, color: '#094053' }}>
              {loading ? '…' : formatInr(target)}
            </div>
          </div>
          <div style={COL_DIVIDED_STYLE}>
            <div style={COL_LABEL_STYLE}>Achievement</div>
            <div style={BIG_ROW_STYLE}>
              <span style={{ ...BIG_NUM_STYLE, color: '#4338ca' }}>
                {loading ? '…' : formatInr(achievement)}
              </span>
              <span style={PCT_PILL_STYLE}>{achievementPct.toFixed(1)}%</span>
            </div>
          </div>
          <div style={COL_DIVIDED_STYLE}>
            <div style={COL_LABEL_STYLE}>Files converted (Won)</div>
            <div style={BIG_ROW_STYLE}>
              <span style={{ ...BIG_NUM_STYLE, color: '#047857' }}>
                {loading ? '…' : formatCount(won)}
              </span>
              <span style={SUB_STYLE}>avg {formatInr(avgPerFile)} / file</span>
            </div>
          </div>
          <div style={COL_DIVIDED_STYLE}>
            <div style={COL_LABEL_STYLE}>Short</div>
            <div style={BIG_ROW_STYLE}>
              <span style={{ ...BIG_NUM_STYLE, color: '#094053' }}>
                {loading ? '…' : formatInr(short)}
              </span>
              <span style={SUB_STYLE}>
                {isCurrentMonth ? `${daysLeft} days left` : 'month closed'}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ height: 8, borderRadius: 4, background: '#eef2ff', overflow: 'hidden' }}>
            <div
              style={{
                width: `${Math.min(100, Math.max(0, achievementPct))}%`,
                // Any non-zero achievement must stay visible: a tiny percentage
                // (e.g. 0.2% of a crore-scale target) renders a sub-pixel fill
                // that the bar's rounded corners hide, so it reads as 0%. Floor
                // the drawn width to a small sliver whenever there is progress.
                minWidth: achievement > 0 ? 6 : 0,
                height: '100%',
                background: '#4338ca',
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: '4px 12px',
              fontSize: 11,
              color: '#71717a',
            }}
          >
            <span>{loading ? '…' : formatInr(achievement)}</span>
            {isCurrentMonth && paceNeeded > 0 ? (
              <span style={{ whiteSpace: 'nowrap' }}>
                Pace needed:{' '}
                <b style={{ color: '#094053', fontWeight: 600 }}>{formatInr(paceNeeded)} / day</b>
              </span>
            ) : null}
            <span>{loading ? '…' : formatInr(target)}</span>
          </div>
        </div>
        {secondary ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 16px',
              fontSize: 11,
              color: '#71717a',
              borderTop: '1px solid #f4f4f5',
              paddingTop: 10,
            }}
          >
            <span>
              Team target{' '}
              <b style={{ color: '#094053', fontWeight: 600 }}>
                {formatInr(secondary.targetAmount)}
              </b>
            </span>
            <span>
              Achieved{' '}
              <b style={{ color: '#094053', fontWeight: 600 }}>
                {formatInr(secondary.achievementAmount)}
              </b>
            </span>
            <span>
              Won{' '}
              <b style={{ color: '#094053', fontWeight: 600 }}>
                {formatCount(secondary.wonLeadCount)}
              </b>
            </span>
          </div>
        ) : null}
      </div>
      {/* Only when a non-default range is active: the count tile follows the
          range but the money tiles are monthly, so spell that out. */}
      {wonWindowActive && rangeLabel ? (
        <p className="mt-1.5 text-[11px] leading-tight text-zinc-500">
          Files Converted (Won) reflects{' '}
          <span className="font-medium text-zinc-600">{rangeLabel}</span>. Target, Achievement and
          Short are for the current month.
        </p>
      ) : null}
    </div>
  );
};
